import React from 'react'
import { dayjs } from '@mobi/utils/date'
import { Currency } from '@mobi/utils/money'
import { FixedOddsMarketCodes } from '@mobi/api-types'
import { Icon as IconLegacy, type IconType } from '@mobi/component-library/Legacy/Icon'
import { BetCard, LinkInfo } from '@mobi/betslip/Components/Common/BetCard'
import { CountdownBadge } from '@mobi/component-library/Feedback/CountdownBadge'
import { StatusBadge } from '@mobi/component-library/Feedback/StatusBadge'
import type {
  Race,
  Acceptor,
  RaceDetails,
  BetSlipItem,
  BettingType,
  Selection,
  FobPropositionSelection,
  ToteSelection,
  RacingBetType,
} from '@mobi/betslip/types'
import {
  isFobPropositionSelection,
  isNoveltyBetType,
  isToteSelection,
  isFobMatchedSelection,
  isSameRaceMultiSelection,
  isStartingPriceMatchedSelection,
} from '@mobi/betslip/helpers/typeGuards'
import { calculateBoosts } from '@mobi/betslip/helpers/calculator/misc'
import { SelectionInfoMultiple } from '@mobi/betslip/Components/Common/BetCard/Components/SelectionInfoMultiple'
import { RacingTitle } from '@mobi/betslip/Components/Common/BetCard/Components/EventDetails/Components/RacingTitle'
import {
  addAcceptorNameToSingleSelection,
  getAcceptorTextForSingleSelection,
  createNoveltyMultipleSelectionMap,
  createMultipleRaceMultipleSelectionMap,
} from './helpers'
import { SilkImage } from '@mobi/betslip/Components/Common/SilkImage/SilkImage'
import { useAppSelector } from '@mobi/betslip/Store/hooks'
import { BetCardTitle } from '@mobi/betslip/Components/Common/BetCardTitle'
import { ShareBetContext } from '@mobi/betslip/context'
import { selectWorkflowStatus } from '@mobi/betslip/Store/Workflow/selectors'
import { MultiLegTypeToggle } from '@mobi/betslip/Components/Multi/Components/MultiLegTypeToggle'
import { MultiLegCheckbox } from '@mobi/betslip/Components/Multi/Components/MultiLegCheckbox'
import { isFatalErrorType } from '@mobi/betslip/helpers/state'
import type { BetInfoDisplayType } from '../../types'

const FixedOddsMarketDisplayNames = {
  [FixedOddsMarketCodes.HeadToHead]: 'Head to Head',
  [FixedOddsMarketCodes.RunnerVsField]: 'Runner vs Field',
  [FixedOddsMarketCodes.StartingPrice]: 'SP - Fixed',
} as const

const mapCodeToIcon: Record<string, IconType> = {
  Races: 'races',
  Trots: 'trot',
  Dogs: 'dog',
}

export const RaceBetInfo: React.FC<{
  item: BetSlipItem
  isMultiItem?: boolean
  displayMode?: BetInfoDisplayType
}> = ({ item, isMultiItem = false, displayMode }) => {
  const workflowStatus = useAppSelector(selectWorkflowStatus)

  const isShareDisplay = React.useContext(ShareBetContext)

  const { selection } = item
  const bettingType = item.bettingType === null ? 'tote-racing' : item.bettingType
  const { races = [], acceptors = [] } = (item.selectionDetails || {}) as RaceDetails

  const hasFatalError = isFatalErrorType(item.betErrorType)

  const { meetingName, raceTime, meetingCode } = races[0] as Race
  const raceStartTime = raceTime
  const racingIconCode = meetingCode

  const iconType = mapCodeToIcon[racingIconCode] || 'tab'

  const shouldDisplayCountdown =
    !hasFatalError &&
    raceStartTime &&
    (!isMultiItem ||
      (isMultiItem && dayjs(raceStartTime).isSameOrBefore(dayjs().add(5, 'minutes'))))

  const isTote = isToteSelection(selection)
  const isMultipleRaceSelection = isTote && isMultiRaceBetType(selection.betType)
  const isSingleRaceNoveltyBet = isTote && isSingleRaceNoveltyBetType(selection.betType)

  const shouldShowPriceInSelectionInfo =
    isMultiItem || workflowStatus == 'proposed' || workflowStatus == 'bets-placed' || isShareDisplay

  const urlPath = item.selectionDetails.urlPath
  const linkInfo: LinkInfo | undefined =
    !isMultiItem && workflowStatus === 'ready' && urlPath ? { pathname: urlPath } : undefined

  return (
    <>
      {(displayMode === 'full' || displayMode === 'event') && (
        <>
          {(isSingleRaceNoveltyBet || isMultipleRaceSelection) && (
            <BetCardTitle title={selection.betType} />
          )}

          {isSameRaceMultiSelection(selection) ? (
            <BetCardTitle
              title={`${selection.acceptors.length} Leg Same Race Multi`}
              rightComponent={selection?.winPrice?.toFixed(2)}
            />
          ) : null}

          <BetCard.EventDetails
            linkInfoPrimary={linkInfo}
            heading={
              <RacingTitle
                races={races}
                meetingName={meetingName}
                isMultipleRaceSelection={isMultipleRaceSelection}
              />
            }
            subHeading={getFixedOddsMarketName(selection)}
            statusComponent={
              <>
                {isShareDisplay ? (
                  <StatusBadge color='gray' badgeSize='S'>
                    {dayjs(raceStartTime).format('D MMM | H:mm')}
                  </StatusBadge>
                ) : shouldDisplayCountdown && raceStartTime ? (
                  <CountdownBadge advertisedStartTime={new Date(raceStartTime)} />
                ) : null}

                {isMultiItem &&
                  !isShareDisplay &&
                  !hasFatalError &&
                  item.shouldAllowPlaceInvestment && (
                    <MultiLegTypeToggle id={item.id} multiLegBetType={item.multiLegBetType} />
                  )}
              </>
            }
            iconComponent={<IconLegacy size='2rem' type={iconType} />}
          />
        </>
      )}

      {(() => {
        if (!selection || displayMode === 'event') return null

        const fixedOrToteDisplay = isMultiItem ? '' : getFixedOrToteDisplay(selection, bettingType)

        const betTypeOverride = isMultiItem
          ? item.multiLegBetType
          : getBetTypeOverrideFromInvestment(item.investment)
        const selectionType = `${getMarketTitle(selection, betTypeOverride)} ${fixedOrToteDisplay}`

        if (isTote) {
          if (isSingleRaceNoveltyBetType(selection.betType)) {
            const noveltyPlacedMap = createNoveltyMultipleSelectionMap(selection, acceptors)
            return <SelectionInfoMultiple selections={noveltyPlacedMap} />
          }

          if (isMultipleRaceSelection) {
            const multipleRaceSelectionMap = createMultipleRaceMultipleSelectionMap(
              selection,
              races,
              acceptors
            )
            return <SelectionInfoMultiple selections={multipleRaceSelectionMap} />
          }

          const heading = addAcceptorNameToSingleSelection(selection.selectionString, acceptors)
          const isField = typeof heading === 'string' && heading.toLowerCase() === 'fd'

          const selectedAcceptor = extractSelectedAcceptor(selection.selectionString, acceptors)

          return (
            <BetCard.SelectionInfo
              iconComponent={
                !selectedAcceptor || isField ? null : <SilkImage acceptor={selectedAcceptor} />
              }
              heading={isField ? <span>FD</span> : heading}
              subHeading={selectionType}
              linkInfo={linkInfo}
            />
          )
        }

        if (isSameRaceMultiSelection(selection)) {
          const sorted = selection.acceptors
            .map(a => ({
              ...a,
              legName: a.legNumber === 0 ? 'WIN' : `TOP ${a.legNumber + 1}`,
            }))
            .sort(sameRaceMultiSortCallback)
          return sorted.map((selectionAcceptor, index) => {
            const number = selectionAcceptor.acceptorNumber.toString()

            return (
              <BetCard.SelectionInfo
                key={index}
                iconComponent={<SilkImage acceptor={extractSelectedAcceptor(number, acceptors)} />}
                heading={addAcceptorNameToSingleSelection(number, acceptors)}
                subHeading=''
                rightAlignedComponent={selectionAcceptor.legName}
                linkInfo={linkInfo}
              />
            )
          })
        }

        if (isFobMatchedSelection(selection)) {
          const [winBoost, placeBoost] = calculateBoosts(item.selectedSuperPickOffer ?? null)

          const {
            winPrice = null,
            placePrice = null,
            isWinBoosted = false,
            isPlaceBoosted = false,
          } = shouldShowPriceInSelectionInfo
            ? getWinPlacePrices({
                isMultiItem,
                isShareDisplay,
                winPrice: selection.winPrice,
                placePrice: selection.placePrice,
                multiLegBetType: item.multiLegBetType,
                winInvestment: item.investment.win.value,
                placeInvestment: item.investment.place.value,
                winBoost,
                placeBoost,
              })
            : {}

          return (
            <BetCard.SelectionInfo
              key={`${item.id}${isMultiItem ? item.multiLegBetType : ''}`}
              iconComponent={
                <SilkImage
                  acceptor={extractSelectedAcceptor(selection.acceptorNumber.toString(), acceptors)}
                />
              }
              heading={addAcceptorNameToSingleSelection(
                selection.acceptorNumber.toString(),
                acceptors
              )}
              subHeading={selectionType}
              priceWin={winPrice}
              priceWinLastSeen={
                selection.winPrice === selection.winPriceLastSeen
                  ? winPrice
                  : selection.winPriceLastSeen
              }
              isPriceWinBoosted={isWinBoosted}
              pricePlace={placePrice}
              pricePlaceLastSeen={
                selection.placePrice === selection.placePriceLastSeen
                  ? placePrice
                  : selection.placePriceLastSeen
              }
              isPricePlaceBoosted={isPlaceBoosted}
              rightAlignedComponent={isMultiItem ? <MultiLegCheckbox item={item} /> : null}
              linkInfo={linkInfo}
            />
          )
        }

        if (isStartingPriceMatchedSelection(selection)) {
          const acceptor = selection.selectedAcceptor
          if (acceptor) {
            return (
              <BetCard.SelectionInfo
                iconComponent={<SilkImage acceptor={acceptor} />}
                heading={getAcceptorTextForSingleSelection(acceptor) || ''}
                subHeading={selectionType}
                linkInfo={linkInfo}
              />
            )
          }
        }

        if (isFobPropositionSelection(selection)) {
          const acceptor = selection.selectedAcceptors && selection.selectedAcceptors[0]
          if (acceptor) {
            const {
              winPrice = null,
              placePrice = null,
              isWinBoosted = false,
              isPlaceBoosted = false,
            } = shouldShowPriceInSelectionInfo
              ? getWinPlacePrices({
                  isMultiItem,
                  isShareDisplay,
                  winPrice: selection.winPrice,
                  placePrice: selection.placePrice,
                  multiLegBetType: item.multiLegBetType,
                  winInvestment: item.investment.win.value,
                  placeInvestment: item.investment.place.value,
                })
              : {}

            return (
              <BetCard.SelectionInfo
                iconComponent={<SilkImage acceptor={acceptor} />}
                heading={getAcceptorTextForSingleSelection(acceptor) || ''}
                subHeading={selectionType}
                priceWin={winPrice}
                priceWinLastSeen={selection.winPriceLastSeen}
                isPriceWinBoosted={isWinBoosted}
                pricePlace={placePrice}
                pricePlaceLastSeen={selection.placePriceLastSeen}
                isPricePlaceBoosted={isPlaceBoosted}
                rightAlignedComponent={isMultiItem && <MultiLegCheckbox item={item} />}
                linkInfo={linkInfo}
              />
            )
          } else {
            return (
              <BetCard.SelectionInfo
                heading={selection.displayName || ''}
                subHeading={selection.marketName}
                linkInfo={linkInfo}
              />
            )
          }
        }

        return null
      })()}
    </>
  )
}

// =============
// Local Helpers
// =============

function getWinPlacePrices({
  isMultiItem,
  isShareDisplay,
  multiLegBetType,
  winPrice,
  placePrice,
  winInvestment,
  placeInvestment,
  winBoost = 0,
  placeBoost = 0,
}: {
  isShareDisplay: boolean
  isMultiItem: boolean
  multiLegBetType: BetSlipItem['multiLegBetType']
  winPrice: number
  placePrice: number | null
  winInvestment: number
  placeInvestment: number
  winBoost?: number
  placeBoost?: number
}): { winPrice?: number; placePrice?: number; isWinBoosted?: boolean; isPlaceBoosted?: boolean } {
  if (isMultiItem) {
    if (multiLegBetType === 'W') return { winPrice }
    if (multiLegBetType === 'P') return { placePrice: placePrice ?? 0 }
    return {}
  }

  let newWinPrice
  let newPlacePrice
  let isWinBoosted = false
  let isPlaceBoosted = false

  const shouldForcePriceDisplay = isShareDisplay && !winInvestment && !placeInvestment

  if (winInvestment || shouldForcePriceDisplay) {
    newWinPrice = new Currency(winPrice).add(winBoost).value
    isWinBoosted = winPrice !== newWinPrice
  }
  if (placeInvestment || shouldForcePriceDisplay) {
    newPlacePrice = new Currency(placePrice ?? 0).add(placeBoost).value
    isPlaceBoosted = placePrice !== newPlacePrice
  }

  return { winPrice: newWinPrice, isWinBoosted, placePrice: newPlacePrice, isPlaceBoosted }
}

function getFixedOddsMarketName(selection: Selection | null, isMultiDisplay = false) {
  const isFobProposition = isFobPropositionSelection(selection)
  // Only display market name to add clarity where starter can appear in multiple markets
  const shouldDisplay =
    isFobProposition &&
    ((isMultiDisplay && selection.marketCode === FixedOddsMarketCodes.Concession) ||
      selection.marketCode === FixedOddsMarketCodes.RunnerVsField ||
      selection.marketCode === FixedOddsMarketCodes.HeadToHead)

  const marketName =
    shouldDisplay &&
    (FixedOddsMarketDisplayNames[
      selection.marketCode as keyof typeof FixedOddsMarketDisplayNames
    ] ||
      selection.marketName)

  return shouldDisplay ? `${isMultiDisplay ? ' - ' : ''}${marketName}` : ''
}

function getMarketTitle(selection: Selection, betTypeOverride?: BetSlipItem['multiLegBetType']) {
  if ((selection as ToteSelection).betType) return (selection as ToteSelection).betType
  if ((selection as FobPropositionSelection).marketCode) {
    return (selection as FobPropositionSelection).marketName
  }
  if (betTypeOverride === 'W') {
    return 'Win'
  }
  if (betTypeOverride === 'P') {
    return 'Place'
  }
  return 'Win & Place'
}

function getBetTypeOverrideFromInvestment(
  investment: BetSlipItem['investment']
): BetSlipItem['multiLegBetType'] {
  if (investment.win.value && !investment.place.value) return 'W'
  if (!investment.win.value && investment.place.value) return 'P'
  return undefined
}

function getFixedOrToteDisplay(selection: Selection, bettingType: BettingType) {
  if (isNoveltyBetType((selection as ToteSelection).betType)) {
    return ''
  }
  if (bettingType === 'fixed-odds-racing' && !(selection as FobPropositionSelection).marketCode) {
    return ' - Fixed'
  }
  if (bettingType === 'tote-racing') {
    return ' - Tote'
  }
  return ''
}

function sameRaceMultiSortCallback(
  a: { legNumber: number; acceptorNumber: number },
  b: { legNumber: number; acceptorNumber: number }
) {
  const diff = a.legNumber - b.legNumber
  if (diff != 0) return diff
  return a.acceptorNumber - b.acceptorNumber
}

function extractSelectedAcceptor(
  selectionNumber: string,
  acceptors: Acceptor[]
): Acceptor | undefined {
  return acceptors.find(a => `${a.number}` === selectionNumber)
}

function isSingleRaceNoveltyBetType(value: RacingBetType): boolean {
  const noveltyTypes: RacingBetType[] = ['Exacta', 'Quinella', 'Trifecta', 'First 4']
  return noveltyTypes.includes(value)
}

function isMultiRaceBetType(value: RacingBetType): boolean {
  return value === 'Double' || value === 'Quaddie' || value === 'All Up'
}
